import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, CircleNotch, Truck, Wrench, GearSix, CheckCircle, WarningCircle, Clock } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = { vehicle: Truck, tool: Wrench, equipment: GearSix };
const STATUS_CONFIG = {
  good: { color: "#34C759", label: "GOOD", icon: CheckCircle },
  due_soon: { color: "#FFCC00", label: "DUE SOON", icon: Clock },
  overdue: { color: "#FF5A00", label: "OVERDUE", icon: WarningCircle },
};

function MaintenanceCard({ item }) {
  const TypeIcon = TYPE_ICONS[item.asset_type] || GearSix;
  const status = STATUS_CONFIG[item.status] || STATUS_CONFIG.good;
  const StatusIcon = status.icon;

  return (
    <div
      data-testid={`maintenance-item-${item.id}`}
      className="wp-card animate-slide-up"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-[#F4F4F5]">
            <TypeIcon size={16} className="text-[#404040]" />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>{item.asset_name}</h3>
            <span className="font-mono-data text-[10px] text-[#404040]">{item.asset_type.toUpperCase()}</span>
          </div>
        </div>
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono-data" style={{ color: status.color, background: `${status.color}10`, border: `1px solid ${status.color}` }}>
          <StatusIcon size={10} weight="fill" />
          {status.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-[#404040]">
        <div>
          <span className="font-mono-data text-[10px] block">LAST SERVICE</span>
          {item.last_service_date || "N/A"}
        </div>
        <div>
          <span className="font-mono-data text-[10px] block">NEXT SERVICE</span>
          {item.next_service_date || "N/A"}
        </div>
      </div>
      {item.notes && (
        <p className="text-xs text-[#404040] mt-2 border-t border-[#E5E5E5] pt-2">{item.notes}</p>
      )}
    </div>
  );
}

export default function MaintenancePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [form, setForm] = useState({
    asset_name: "", asset_type: "vehicle", last_service_date: "",
    next_service_date: "", service_interval_days: 90, notes: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") params.asset_type = filterType;
      const res = await axios.get(`${API}/maintenance`, { params });
      setItems(res.data);
    } catch {
      toast.error("Failed to load maintenance items");
    }
    setLoading(false);
  }, [filterType]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const overdue = items.filter(i => i.status === "overdue");
  const dueSoon = items.filter(i => i.status === "due_soon");

  const createItem = async (e) => {
    e.preventDefault();
    if (!form.asset_name) { toast.error("Asset name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/maintenance`, {
        ...form, service_interval_days: parseInt(form.service_interval_days)
      });
      toast.success("Maintenance item added");
      setCreateOpen(false);
      setForm({ asset_name: "", asset_type: "vehicle", last_service_date: "", next_service_date: "", service_interval_days: 90, notes: "" });
      fetchItems();
    } catch {
      toast.error("Failed to add item");
    }
    setSaving(false);
  };

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="maintenance-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Maintenance
          </h1>
          <p className="text-[10px] font-mono-data text-[#404040]">
            VEHICLES & TOOLS
            {overdue.length > 0 && <span className="text-[#FF5A00] ml-2">{overdue.length} OVERDUE</span>}
          </p>
        </div>
        <button
          data-testid="add-maintenance-btn"
          onClick={() => setCreateOpen(true)}
          className="touch-target w-10 h-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center hover:bg-[#001D6C] transition-colors"
        >
          <Plus size={20} weight="bold" />
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {["all", "vehicle", "tool", "equipment"].map(type => (
          <button
            key={type}
            data-testid={`filter-maint-${type}`}
            onClick={() => setFilterType(type)}
            className={`px-3 py-1.5 text-xs font-bold rounded-sm border transition-colors ${
              filterType === type
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                : "bg-white text-[#404040] border-[#E5E5E5] hover:border-[#0A0A0A]"
            }`}
          >
            {type === "all" ? "ALL" : type.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Summary */}
      {(overdue.length > 0 || dueSoon.length > 0) && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="wp-card">
            <div className="metric-label">OVERDUE</div>
            <div className="metric-value" style={{ color: overdue.length > 0 ? "#FF5A00" : "#34C759" }}>
              {overdue.length}
            </div>
          </div>
          <div className="wp-card">
            <div className="metric-label">DUE SOON</div>
            <div className="metric-value" style={{ color: dueSoon.length > 0 ? "#FFCC00" : "#34C759" }}>
              {dueSoon.length}
            </div>
          </div>
        </div>
      )}

      {/* Items */}
      {loading ? (
        <div className="flex justify-center py-12">
          <CircleNotch size={28} className="animate-spin text-[#002FA7]" />
        </div>
      ) : items.length === 0 ? (
        <div className="wp-card text-center py-8 text-sm text-[#404040]">
          No maintenance items. Add vehicles, tools, or equipment to track.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={item.id} style={{ animationDelay: `${i * 0.05}s` }}>
              <MaintenanceCard item={item} />
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Add Maintenance Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={createItem} className="space-y-3">
            <input
              data-testid="maint-name-input"
              type="text" placeholder="Asset name (e.g., Van #1)"
              value={form.asset_name} onChange={e => setForm({...form, asset_name: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <Select value={form.asset_type} onValueChange={v => setForm({...form, asset_type: v})}>
              <SelectTrigger data-testid="maint-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vehicle">Vehicle</SelectItem>
                <SelectItem value="tool">Tool</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono-data text-[#404040] block mb-1">LAST SERVICE</label>
                <input
                  data-testid="maint-last-date"
                  type="date" value={form.last_service_date}
                  onChange={e => setForm({...form, last_service_date: e.target.value})}
                  className="w-full px-2 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono-data text-[#404040] block mb-1">NEXT SERVICE</label>
                <input
                  data-testid="maint-next-date"
                  type="date" value={form.next_service_date}
                  onChange={e => setForm({...form, next_service_date: e.target.value})}
                  className="w-full px-2 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono-data text-[#404040] block mb-1">SERVICE INTERVAL (DAYS)</label>
              <input
                data-testid="maint-interval-input"
                type="number" value={form.service_interval_days}
                onChange={e => setForm({...form, service_interval_days: e.target.value})}
                className="w-full px-2 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            <textarea
              data-testid="maint-notes-input"
              placeholder="Notes (optional)"
              value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-16"
            />
            <button
              data-testid="create-maintenance-submit"
              type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50"
            >
              {saving ? "Adding..." : "Add Item"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
